import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import { sendQuoteToClient } from '../services/quoteService';
import { apiClient } from '@/lib/api';
import { formatDateToISO, formatDateToISOWithTimezone } from '@/utils';
import { ROUTES } from '../../../constants';
import { getAirportTimezone } from '@utils/airportTimezone';
import { toast } from "sonner";
import { trunc2 } from '@/utils';
/**
 * Build leg itinerary array from flights
 */
const buildLegItinerary = (flights) =>
  (flights || []).map((flight) => ({
    pax: flight.passengerCount || 1,
    departure_date: formatDateToISOWithTimezone(flight.departureDate, flight.departureTime, getAirportTimezone(flight.departure?.icao)) || null,
    arrival_address: null,
    arrival_airport: flight.arrival?.icao || "",
    departure_address: null,
    departure_airport: flight.departure?.icao || "",
  }));

/**
 * Extract client name from quote data
 */
const getClientName = (clientData) =>
  [clientData?.firstName || clientData?.first_name, clientData?.lastName || clientData?.last_name]
    .filter(Boolean).join(' ') || "";

/**
 * Build bid payload from quote data
 */
const buildBidPayload = (quoteData, { finalPrice, createdBy, brokerOrgId }) => ({
  trip_number: `T${Math.floor(Math.random() * 1000000)}`,
  bid_number: `Q${Date.now()}`,
  created_by: createdBy,
  broker_id: createdBy,
  broker_org_id: brokerOrgId,
  client_name: getClientName(quoteData.client?.clientData),
  client_email: quoteData.client?.clientData?.email || "",
  client_phone: quoteData.client?.clientData?.phone || "",
  departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
  arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
  departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  return_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.returnDate, quoteData.flights?.[0]?.returnTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
  baggage_requirements: quoteData.flights?.[0]?.notes || "",
  special_requests: quoteData.settings?.notes || "",
  status: "DRAFT",
  total_amount: trunc2(parseFloat(finalPrice) || 0),
  currency: "USD",
  valid_until: quoteData.settings?.validUntil ? formatDateToISO(quoteData.settings.validUntil) : null,
  sent_at: null,
  viewed_at: null,
  accepted_at: null,
  notes: quoteData.settings?.notes || "",
  leg_itinerary: buildLegItinerary(quoteData.flights),
});

/**
 * Build quote payload with pricing fields
 */
const buildQuotePayload = (quoteData, { brokerOrgId, createdBy, bidItems, status, settings }) => {
  const airportZone = getAirportTimezone(quoteData.flights?.[0]?.departure?.icao);
  return {
    ...quoteData,
    broker_org_id: brokerOrgId,
    created_by: createdBy,
    bid_items: bidItems,
    departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, airportZone) || null,
    return_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.returnDate, quoteData.flights?.[0]?.returnTime, airportZone) || (quoteData.flightType === "round-trip" ? formatDateToISOWithTimezone(quoteData.flights?.[1]?.departureDate, quoteData.flights?.[1]?.departureTime, airportZone) : null),
    total_due: settings?.total_cost || 0,
    broker_margin: settings?.broker_margin || 0,
    platform_fee: settings?.platform_fee || 0,
    federal_excise_tax: settings?.federal_excise_tax || 0,
    segment_tax: settings?.segment_tax || 0,
    operator_cost: settings?.operator_cost || 0,
    taxable_amount: settings?.taxable_amount || 0,
    card_fee: settings?.card_fee || 0,
    status
  };
};

/**
 * Build bid update payload for existing trip group bids
 */
const buildBidUpdatePayload = (quoteData, finalPrice) => ({
  client_name: getClientName(quoteData.client?.clientData),
  client_email: quoteData.client?.clientData?.email || "",
  client_phone: quoteData.client?.clientData?.phone || "",
  departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
  arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
  departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  return_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.returnDate, quoteData.flights?.[0]?.returnTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
  baggage_requirements: quoteData.flights?.[0]?.notes || "",
  special_requests: quoteData.settings?.notes || "",
  status: "SENT",
  total_amount: trunc2(finalPrice || 0),
  currency: "USD",
  valid_until: quoteData.settings?.validUntil ? formatDateToISO(quoteData.settings.validUntil) : null,
  sent_at: null,
  viewed_at: null,
  accepted_at: null,
  notes: quoteData.settings?.notes || "",
  leg_itinerary: buildLegItinerary(quoteData.flights),
});

/**
 * Create bid options for a given bid
 */
const createBidOptions = async (bidId, bidOptions) => {
  if (!bidOptions || bidOptions.length === 0) return;
  const promises = bidOptions.map((option, index) => {
    const payload = {
      bid_id: bidId,
      aircraft_id: option?.aircraft_id || "",
      operator_id: option?.operator_id || "",
      base_price: "0.00",
      fuel_cost: "0.00",
      landing_fees: "0.00",
      crew_fees: "0.00",
      catering_cost: "0.00",
      ground_transport: "0.00",
      other_fees: "0.00",
      total_price: trunc2(option?.total_price || 0),
      broker_markup: trunc2(option?.broker_markup || 0),
      final_price: trunc2(option?.final_price || 0),
      federal_excise_tax: trunc2(option?.federal_excise_tax || 0),
      segment_tax: trunc2(option?.segment_tax || 0),
      platform_fee: trunc2(option?.platform_fee || 0),
      operator_cost: trunc2(option?.operator_cost || 0),
      is_recommended: index === 0,
      recommendation_reason: index === 0 ? "." : "",
      availability_confirmed: true,
      shortlisted: true,
      risk_level: "LOW",
      trust_score: "0.00",
      estimated_flight_time: 0,
      aircraft_features: { beds: 1, wifi: false, entertainment: false }
    };
    return apiClient.createBidOption(payload);
  });
  await Promise.all(promises);
};

/**
 * Update existing bid options via API
 */
const updateExistingBidOptions = async (bidOptions) => {
  if (!bidOptions || bidOptions.length === 0) return;
  const promises = bidOptions.map(async (option) => {
    const bidOptionId = option.bid_option_id;
    if (!bidOptionId) {
      console.warn('Skipping bid option without ID:', option);
      return null;
    }
    const { segment_pax_count, segment_fee_per_leg, ...optionToSend } = option;
    return apiClient.updateBidOption(bidOptionId, optionToSend);
  });
  await Promise.all(promises);
};

/**
 * Hook that handles all quote processing (draft saving & sending)
 */
const useQuoteProcessor = (organization = null) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { shortlistAircraft, selectedTripGroup } = useAppStore();
  const { userProfile } = useAuthStore();

  const [isSending, setIsSending] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [error, setError] = useState(null);

  const processQuote = useCallback(async (quoteData, { status, send }) => {
    const finalPrice = quoteData.settings.final_price || 0;
    const bidOptions = quoteData.bidOptions || [];
    
    const brokerOrgId = userProfile?.org_id;
    const createdBy = userProfile?.userprofile_id;

    let createdQuote;
    let createdBid;

    if (shortlistAircraft) {
      // New bid from shortlist
      const bidPayload = buildBidPayload(quoteData, { finalPrice, createdBy, brokerOrgId });
      createdBid = await apiClient.createBid(bidPayload);

      await createBidOptions(createdBid.bid_id, bidOptions);

      const fullQuoteData = buildQuotePayload(quoteData, {
        brokerOrgId: bidPayload.broker_org_id,
        createdBy: bidPayload.created_by,
        bidItems: [{ bid_id: createdBid.bid_id }],
        status,
        settings: quoteData.settings
      });

      if (send) {
        createdQuote = await sendQuoteToClient(fullQuoteData, queryClient, organization);
      } else {
        createdQuote = await apiClient.createQuote(fullQuoteData);
      }

      await apiClient.updateBid(createdBid.bid_id, { status: 'SENT' });

    } else {
      // Existing trip group bid
      await updateExistingBidOptions(bidOptions);

      const fullQuoteData = buildQuotePayload(quoteData, {
        brokerOrgId: userProfile?.org_id || brokerOrgId,
        createdBy,
        bidItems: selectedTripGroup?.bids?.map(bid => ({ bid_id: bid.bid_id })) || [],
        status,
        settings: quoteData.settings
      });

      if (send) {
        createdQuote = await sendQuoteToClient(fullQuoteData, queryClient, organization);
      } else {
        createdQuote = await apiClient.createQuote(fullQuoteData);
      }

      const selectedBidId = selectedTripGroup?.bids?.[0]?.bid_id;
      if (selectedBidId) {
        await apiClient.updateBid(selectedBidId, buildBidUpdatePayload(quoteData, finalPrice));
      }
    }

    // Upload contract file and save to org branding history if admin
    const contractFile = quoteData.settings?.contractFile?.file;
    let contractPath = quoteData.settings?.contractFile?.path;
    if (contractFile) {
      const quoteId = createdQuote?.quote_id || `Q${Date.now()}`;
      const renamed = new File([contractFile], `quote_${quoteId}_${contractFile.name}`, { type: contractFile.type });
      const uploaded = await apiClient.uploadFile(renamed, "contract");
      contractPath = uploaded.file_path;

      // Save contract path to quote record
      const quoteRecordId = createdQuote?.quote_id;
      if (quoteRecordId) {
        await apiClient.updateQuote(quoteRecordId, { files: { contract: contractPath } });
      }
    }
    if (contractPath && ['admin', 'ADMIN'].includes(userProfile?.role) && organization) {
      const existingHistory = organization?.branding?.contractHistory || [];
      await apiClient.updateOrganization(brokerOrgId, {
        branding: {
          ...organization?.branding,
          contractHistory: [...new Set([contractPath, ...existingHistory])],
        },
      });
    }

    // Optimistically seed the quotes cache so QuotesList renders the new quote
    // instantly when the user lands on /quotes (its background refetch will
    // reconcile shortly after).
    const optimisticBidItems = shortlistAircraft
      ? [{
          bid: {
            ...createdBid,
            bid_options: bidOptions.map(opt => ({ ...opt, shortlisted: true })),
          },
        }]
      : (selectedTripGroup?.bids || []).map(bid => ({
          bid: {
            ...bid,
            bid_options: (bid.bid_options || []).map(opt => ({ ...opt, shortlisted: true })),
          },
        }));

    const optimisticQuote = {
      ...createdQuote,
      id: createdQuote?.quote_id || createdQuote?.id,
      status: createdQuote?.status || status,
      client: createdQuote?.client || quoteData.client?.clientData || {},
      bid_items: optimisticBidItems,
    };

    queryClient.setQueriesData(
      { predicate: (q) => q.queryKey[0] === 'quotes' && q.queryKey[1] === 'all' },
      (old) => [optimisticQuote, ...(old || [])]
    );

    // Fire-and-forget: refresh quotes and shortlistedBids in the background so
    // server-truth reconciles without blocking navigation.
    queryClient.invalidateQueries({ queryKey: ['quotes'] });

    toast.success(send ? 'Quote successfuly created' : 'Quote sent to client');
    setTimeout(() => navigate(ROUTES.QUOTES.path), 500);
  }, [shortlistAircraft, selectedTripGroup, userProfile, queryClient, navigate, organization]);

  const handleSaveDraft = useCallback(async (quoteData) => {
    try {
      setIsSavingDraft(true);
      setError(null);
      await processQuote(quoteData, { status: 'DRAFT', send: false });
    } catch (err) {
      setError(err.message || 'Failed to save draft. Please try again.');
      setIsSavingDraft(false);
    }
  }, [processQuote]);

  const handleSendQuote = useCallback(async (quoteData) => {
    try {
      setIsSending(true);
      setError(null);
      await processQuote(quoteData, { status: 'SENT', send: true });
    } catch (err) {
      console.error('sendQuote error:', err);
      setError(err.message || 'Failed to process quote. Please try again.');
      setIsSending(false);
    }
  }, [processQuote]);

  return {
    handleSaveDraft,
    handleSendQuote,
    isSending,
    isSavingDraft,
    error,
    setError,
  };
};

export default useQuoteProcessor;
